import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  Suspense,
  lazy,
} from 'react';
import './profile.css';
import Sidebar from '../../components/sidebar/Sidebar';
import Feed from '../../components/feed/Feed';
import Rightbar from '../../components/rightbar/Rightbar';
import axiosConn from '../../axiosConn';
import LoadingBar from 'react-top-loading-bar';
import { handleProfileAndCoverUpload } from '../../utils/handleProfileAndCoverUpload';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import EasyEdit from 'react-easy-edit';
import { CircularProgress } from '@material-ui/core';
import { handleYouTube } from '../../utils/youtubeUtil';
import MediaCover from '../../components/mediaCover/MediaCover';
import ModalImage from 'react-modal-image';
import useMobileView from '../../hooks/useMobileView';
import { useTheme } from '../../context/ThemeContext';
import {
  DEFAULT_PROFILE_PHOTO_URL,
  needsCoverPhotoCompletion,
  needsProfilePhotoCompletion,
} from '../../utils/profileCompletion';
import LocalOpsHelperProfileCard from '../../components/localops/LocalOpsHelperProfileCard';
import LocalOpsRideReadyCard from '../../components/localops/LocalOpsRideReadyCard';

const BadgeGallery = lazy(() => import('../../components/badges/BadgeGallery'));

function BadgeGallerySkeleton() {
  return (
    <div
      style={{
        padding: '6px 10px 2px',
        display: 'flex',
        gap: 14,
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 80,
            flex: '0 0 auto',
            textAlign: 'center',
            fontSize: 10,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              margin: '0 auto 6px',
              background: 'linear-gradient(120deg,#eceef2,#f5f6fa 60%,#eceef2)',
              animation: 'pulse 1.4s ease-in-out infinite',
            }}
          />
          <div
            style={{
              width: 54,
              height: 8,
              borderRadius: 999,
              background: '#e2e8f0',
              margin: '0 auto',
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function Profile() {
  const { player, updatePlayer } = useContext(AuthContext);
  const imageUploader = useRef(null);
  const coverImageUploader = useRef(null);
  const profileCompletionRef = useRef(null);
  const coverCompletionRef = useRef(null);
  const localOpsHelperProfileRef = useRef(null);
  const localOpsRideReadyRef = useRef(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [player1, setPlayer1] = useState({});
  const playerId = useParams().playerId;
  const [isFetching, setIsFetching] = useState(false);
  const textDefault = '-';
  const [progress, setProgress] = useState(0);
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [isVideo, setIsVideo] = useState(false);
  const [isTube, setIsTube] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(player1.profilePhoto);
  const mobileView = useMobileView();
  const { darkTheme } = useTheme();
  const location = useLocation();

  const handleVideoLoad = () => setVideoLoaded(true);

  useEffect(() => {
    setProfilePhoto(player1.profilePhoto);
  }, [player1.profilePhoto]);

  const handleClick = (tag) => () => {
    if (tag === 'cover') coverImageUploader.current.click();
    else imageUploader.current.click();
  };

  const handleSave = (tag) => async (text) => {
    const currentPlayer = JSON.parse(localStorage.getItem('player'));
    if (['desc', 'city', 'from'].includes(tag)) {
      currentPlayer.data.player[tag] = text !== '' ? text : textDefault;
      try {
        await axiosConn.patch(
          'players/updateMe',
          { [tag]: currentPlayer.data.player[tag] },
          { headers: { Authorization: `Bearer ${player.token}` } },
        );
        localStorage.setItem('player', JSON.stringify(currentPlayer));
        setPlayer1(currentPlayer.data.player);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleCancel = () => {};

  useEffect(() => {
    let mounted = true;
    const fetchPlayer = async () => {
      setIsLoading(true);
      try {
        const res = await axiosConn.get(`players?playerId=${playerId}`, {
          headers: { Authorization: `Bearer ${player.token}` },
        });
        if (!mounted) return;
        setPlayer1(res.data.data.data[0]);
      } catch (e) {
        console.error('Profile fetch error', e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchPlayer();
    return () => {
      mounted = false;
    };
  }, [playerId, player.token, updateTrigger]);

  useEffect(() => {
    if (player1.coverPhoto) {
      handleYouTube(player1.coverPhoto, setIsVideo, setIsTube);
    }
  }, [player1.coverPhoto]);

  // Enforce exactly one visible row of up to 3 badges and make width match count.
  useEffect(() => {
    const root = document.querySelector('.profileSingleRowBadges');
    if (!root) return;

    const COL_WIDTH = 160; // matches BadgeGallery minmax upper bound
    const GAP = 16;
    const MAX_COLS = 3;

    const adjust = () => {
      const items = Array.from(
        root.querySelectorAll('[data-badge-item="true"]'),
      );
      if (!items.length) return;

      // Show only the first MAX_COLS
      items.forEach((el, idx) => {
        el.style.display = idx < MAX_COLS ? '' : 'none';
      });

      const visible = items.slice(0, Math.min(items.length, MAX_COLS));

      // Find nearest grid container
      const grid =
        visible[0]?.parentElement &&
        visible[0].parentElement.hasAttribute('data-badge-item')
          ? visible[0].parentElement.parentElement
          : visible[0]?.parentElement;

      let gridEl = grid;
      if (gridEl && getComputedStyle(gridEl).display !== 'grid') {
        gridEl =
          root.querySelector('div[style*="grid-template-columns"]') || gridEl;
      }

      const cols = visible.length;
      if (gridEl) {
        gridEl.style.gridTemplateColumns = `repeat(${cols},minmax(0,1fr))`;
        gridEl.style.gap = `${GAP}px`;
      }

      // Dynamically size wrapper on desktop (keep fluid on mobile)
      if (window.innerWidth >= 800) {
        const targetWidth = cols * COL_WIDTH + (cols - 1) * GAP;
        root.style.maxWidth = `${targetWidth}px`;
      } else {
        root.style.maxWidth = '100%';
      }
    };

    const mo = new MutationObserver(adjust);
    mo.observe(root, { childList: true, subtree: true });

    adjust();
    window.addEventListener('resize', adjust);
    return () => {
      mo.disconnect();
      window.removeEventListener('resize', adjust);
    };
  }, [player1?._id]);

  // --- NEW: compute self vs other once, reuse across gallery + links
  const isSelf =
    player1?.playerId === player?.data?.player?.playerId; /* your rule */
  const profilePhotoIncomplete =
    isSelf && needsProfilePhotoCompletion(player1);
  const coverPhotoIncomplete = isSelf && needsCoverPhotoCompletion(player1);
  const focusTarget = new URLSearchParams(location.search || '').get('focus');
  const focusProfilePhoto = focusTarget === 'profilePhoto';
  const focusCoverPhoto = focusTarget === 'coverPhoto';
  const focusLocalOpsHelperProfile = focusTarget === 'localOpsHelperProfile';
  const focusLocalOpsRideReady = focusTarget === 'localOpsRideReady';
  const profileInfoClassName = [
    'profileInfo',
    darkTheme ? 'dark-theme blackBg' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const profileRightTopClassName = [
    'profileRightTop',
    profilePhotoIncomplete || coverPhotoIncomplete
      ? 'profileRightTop--photo-incomplete'
      : '',
  ]
    .filter(Boolean)
    .join(' ');
  const profileRightBottomClassName = [
    'profileRightBottom',
    profilePhotoIncomplete || coverPhotoIncomplete
      ? 'profileRightBottom--photo-incomplete'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (!focusProfilePhoto || !profilePhotoIncomplete) return;
    profileCompletionRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [focusProfilePhoto, profilePhotoIncomplete]);

  useEffect(() => {
    if (!focusCoverPhoto || !coverPhotoIncomplete) return;
    coverCompletionRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [focusCoverPhoto, coverPhotoIncomplete]);

  useEffect(() => {
    if (!focusLocalOpsHelperProfile || !isSelf) return;
    localOpsHelperProfileRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [focusLocalOpsHelperProfile, isSelf]);

  useEffect(() => {
    if (!focusLocalOpsRideReady || !isSelf) return;
    localOpsRideReadyRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }, [focusLocalOpsRideReady, isSelf]);

  // Precompute target path for “Open full page”
  const openFullPageTo = isSelf
    ? '/badges'
    : `/p/${encodeURIComponent(player1?.playerId || '')}/badges`;

  return (
    <>
      <div className="profile">
        <LoadingBar
          color="limegreen"
          progress={progress}
          onLoaderFinished={() => setProgress(0)}
        />

        {!mobileView && <Sidebar />}

        <div className="profileRight">
          <div className={profileRightTopClassName}>
            <div className="profileCover">
              <div
                className="CoverImgChange"
                onClick={() => coverImageUploader.current.click()}
                title={
                  player?.data?.player?.playerId ? 'Change cover' : undefined
                }
                style={{ position: 'relative' }}
              >
                <input
                  disabled={
                    isLoading ||
                    player1.playerId !== player?.data?.player?.playerId
                      ? true
                      : undefined
                  }
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleProfileAndCoverUpload({
                    parent: 'player',
                    tag: 'cover',
                    setIsFetching,
                    setProgress,
                    setUploadPercentage,
                    setUpdateTrigger,
                    setPlayer1,
                    updatePlayer,
                    player,
                  })}
                  ref={coverImageUploader}
                  style={{ display: 'none' }}
                />
                <MediaCover
                  isVideo={isVideo}
                  isTube={isTube}
                  mediaSource={player1.coverPhoto}
                  handleVideoLoad={handleVideoLoad}
                  parent="playerProfile"
                  player1={player1}
                />
              </div>

              <div className="profileImgChange">
                <input
                  disabled={player1.playerId !== player?.data?.player?.playerId}
                  type="file"
                  accept="image/*"
                  onChange={handleProfileAndCoverUpload({
                    parent: 'player',
                    tag: 'profile',
                    setIsFetching,
                    setProgress,
                    setUploadPercentage,
                    setUpdateTrigger,
                    setPlayer1,
                    updatePlayer,
                    player,
                    playerId,
                  })}
                  ref={imageUploader}
                  style={{ display: 'none' }}
                />
                {isSelf ? (
                  <img
                    src={
                      profilePhoto
                        ? profilePhoto
                        : DEFAULT_PROFILE_PHOTO_URL
                    }
                    alt=""
                    className="profilePlayerImg"
                    title={`${player1.playerId}, click to change your profile photo`}
                    onClick={handleClick('profile')}
                  />
                ) : (
                  <ModalImage
                    hideDownload
                    small={
                      player1.profilePhoto
                        ? player1.profilePhoto
                        : DEFAULT_PROFILE_PHOTO_URL
                    }
                    large={
                      player1.profilePhoto
                        ? player1.profilePhoto
                        : DEFAULT_PROFILE_PHOTO_URL
                    }
                    className="profilePlayerImg"
                    alt=""
                  />
                )}
              </div>

              <div className="profileChangeProgress">
                {isFetching && <CircularProgress color="inherit" size="20px" />}
              </div>
            </div>

            {coverPhotoIncomplete ? (
              <div className="profile-completion-slot">
                <div
                  className={`profile-completion-card profile-completion-card--cover${
                    focusCoverPhoto ? ' is-focused' : ''
                  }`}
                  data-testid="cover-photo-completion-card"
                  ref={coverCompletionRef}
                >
                  <div>
                    <div className="profile-completion-eyebrow">
                      LocalOps trust
                    </div>
                    <h3>Add your cover photo</h3>
                    <p>
                      Show locals what you are about. Profile updates earn JGK.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="profile-completion-button"
                    data-testid="cover-photo-upload-cta"
                    onClick={handleClick('cover')}
                    disabled={isFetching}
                  >
                    Upload cover photo
                  </button>
                </div>
              </div>
            ) : null}

            {profilePhotoIncomplete ? (
              <div className="profile-completion-slot">
                <div
                  className={`profile-completion-card${
                    focusProfilePhoto ? ' is-focused' : ''
                  }`}
                  data-testid="profile-photo-completion-card"
                  ref={profileCompletionRef}
                >
                  <div>
                    <div className="profile-completion-eyebrow">
                      LocalOps trust
                    </div>
                    <h3>Add your profile photo</h3>
                    <p>
                      Help local helpers recognize you at handoff. Profile updates
                      earn JGK.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="profile-completion-button"
                    data-testid="profile-photo-upload-cta"
                    onClick={handleClick('profile')}
                    disabled={isFetching}
                  >
                    Upload profile photo
                  </button>
                </div>
              </div>
            ) : null}

            {isSelf ? (
              <>
                <div className="profile-completion-slot profile-completion-slot--localops-helper">
                  <LocalOpsHelperProfileCard
                    cardRef={localOpsHelperProfileRef}
                    isFocused={focusLocalOpsHelperProfile}
                    token={player?.token}
                  />
                </div>
                <div className="profile-completion-slot profile-completion-slot--localops-ride-ready">
                  <LocalOpsRideReadyCard
                    cardRef={localOpsRideReadyRef}
                    isFocused={focusLocalOpsRideReady}
                    token={player?.token}
                  />
                </div>
              </>
            ) : null}

            <div
              className={profileInfoClassName}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                isolation: 'isolate',
              }}
            >
              <h4
                className={
                  darkTheme
                    ? 'profileInfoName dark-theme blackBg'
                    : 'profileInfoName'
                }
                style={{
                  margin: '6px 0 6px',
                  fontSize: mobileView ? 22 : 30,
                  lineHeight: 1.15,
                  fontWeight: 700,
                  background:
                    'linear-gradient(90deg,#6366f1,#8b5cf6,#ec4899,#f59e0b)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: '.5px',
                }}
              >
                {player1.playerId}
              </h4>

              <EasyEdit
                value={player1.desc}
                type="text"
                onSave={handleSave('desc')}
                onCancel={handleCancel('desc')}
                saveOnBlur
                attributes={{ name: 'profile-desc', id: 'profile-desc' }}
                className="profileInfoDesc"
                allowEdit={isSelf}
              />

              {player1?._id && (
                <div
                  className="profileSingleRowBadges"
                  data-testid="profile-inline-badges"
                  style={{
                    marginTop: 12,
                    width: '100%',
                    maxWidth: 520, // starting cap (will shrink to exact width for 1–3 items on desktop)
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'max-width .25s ease',
                  }}
                >
                  <Suspense fallback={<BadgeGallerySkeleton />}>
                    <BadgeGallery
                      /** Always fetch badges for the profile owner we’re viewing */
                      playerId={player1._id} // mongoId
                      token={player?.token}
                      limit={3}
                      dense
                      showHeader
                      showRefresh={false}
                      collapseEnabled={false}
                      /** CRITICAL: avoid 401/403 when viewing others */
                      includeLocked={isSelf} // only self can request locked
                      product="" // request all products
                      types=""
                      categories=""
                      /** Make the header’s “Open full page” link point to the owner */
                      openFullPageTo={openFullPageTo} // ← BadgeGallery reads this if present
                      ownerHandle={player1.playerId} // ← and/or this (back-compat with your impl)
                      style={{
                        margin: 0,
                        padding: 0,
                        width: '100%',
                        overflow: 'hidden',
                      }}
                    />
                  </Suspense>

                  {/* Extra guard: if your BadgeGallery doesn’t yet support openFullPageTo,
                      we render a small helper link just below. Safe no-op when it already exists. */}
                  <div style={{ marginTop: 6 }}>
                    <a
                      href={openFullPageTo}
                      style={{
                        fontSize: 12,
                        color: darkTheme ? '#cbd5e1' : '#334155',
                        textDecoration: 'none',
                        border: `1px solid ${
                          darkTheme ? 'rgba(255,255,255,0.12)' : '#e2e8f0'
                        }`,
                        borderRadius: 999,
                        padding: '4px 10px',
                      }}
                      title="Open full badges page"
                    >
                      Open full page ↗
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={profileRightBottomClassName}>
            <Feed playerId={playerId} />
            <Rightbar player={player} player1={player1} />
          </div>
        </div>
      </div>
    </>
  );
}
